import React, { Fragment } from 'react';
import { cn } from '@/lib/utils';

export interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'success';
  typeToConfirm?: string;
}

const variantClasses: Record<NonNullable<ConfirmModalProps['variant']>, string> = {
  danger: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
  warning: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
  info: 'bg-brand-600 hover:bg-brand-700 focus:ring-brand-500',
  success: 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500',
};

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'danger',
  typeToConfirm,
}) => {
  const [typedValue, setTypedValue] = React.useState('');

  React.useEffect(() => {
    if (!isOpen) setTypedValue('');
  }, [isOpen]);

  if (!isOpen) return null;

  const requiresTypeToConfirm = Boolean(typeToConfirm);
  const canConfirm = requiresTypeToConfirm ? typedValue === typeToConfirm : true;

  return (
    <Fragment>
      <div className="fixed inset-0 z-40 bg-slate-900/50 dark:bg-slate-900/70" onClick={onClose} aria-hidden="true" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800">
          <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-700">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">{title}</h3>
          </div>
          <div className="px-5 py-4">
            <p className="text-sm text-slate-600 dark:text-slate-300">{message}</p>
            {requiresTypeToConfirm && (
              <div className="mt-4">
                <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
                  Digite <span className="font-mono font-semibold">{typeToConfirm}</span> para confirmar:
                </p>
                <input
                  type="text"
                  value={typedValue}
                  onChange={(e) => setTypedValue(e.target.value)}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                />
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-3 dark:border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              {cancelText}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={!canConfirm}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                variantClasses[variant],
              )}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </Fragment>
  );
};
